import { apiClient, publicApiClient } from './api';

export interface ReplacementCard {
  id: string;
  customerId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  allowRedisplay: string;
}

// View contracts mirror the parent-owned backend invoice-replacement.types.ts.
export interface ReplacementPaymentResult {
  status: 'paid' | 'requires_action' | 'processing' | 'requires_payment_method';
  invoiceId: string;
  paymentIntentId: string | null;
  clientSecret?: string;
}
// The core public endpoints also return pre-dispatch and uncertain-outcome receipts.
export type ReplacementPaymentReceipt =
  | ReplacementPaymentResult
  | { status: 'ready' }
  | { status: 'unknown'; message?: string };
export interface ReplacementReviewView {
  reviewToken: string;
  fingerprint: string;
  expiresAt: string;
  currency: string;
  total: number;
  customerId: string;
  card: ReplacementCard;
  invoices: Array<{
    id: string;
    number: string;
    amountDue: number;
    historical: boolean;
    storedPaymentUrl: string | null;
    currentPaymentUrl: string | null;
    reservationId: string | null;
  }>;
  warnings: string[];
}
export interface ReplacementBatchView {
  id: string;
  fingerprint: string;
  state: string;
  currency: string;
  total: number;
  reason: string;
  createdAt: string;
  updatedAt: string;
  items: Array<{
    id: string;
    originalId: string;
    originalNumber: string;
    amount: number;
    replacementId: string;
    replacementNumber: string;
    state: string;
    error: string | null;
    paymentUrl: string | null;
    pdfArchived: boolean;
    consentRecorded: boolean;
    paymentStatus: string | null;
  }>;
}
export const REPLACEMENT_CONSENT_VERSION = 'replacement-card-confirmation-v1';

export interface PublicReplacementView {
  itemId: string;
  invoiceNumber: string;
  originalNumber: string;
  amount: number;
  currency: string;
  card: Pick<ReplacementCard, 'brand' | 'last4' | 'expMonth' | 'expYear'>;
  status: string;
  consentVersion: string;
  stripeAccountId: string;
  publishableKey: string;
}

// Match the existing billing-operation bound without changing either global client.
const BILLING_OPERATION_TIMEOUT_MS = 120000;
const billingTimeout = { timeout: BILLING_OPERATION_TIMEOUT_MS };
const base = '/invoice-replacements';
const part = encodeURIComponent;
const batchPath = (id: string) => `${base}/${part(id)}`;
const publicPath = (token: string) => `${base}/public/${part(token)}`;
const portalAuth = (portalToken: string) => ({
  ...billingTimeout,
  headers: { Authorization: `Bearer ${portalToken}` },
});
// No retries and no raw Axios errors: request objects can contain bearer links and credentials.
async function safe<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data;
  } catch (failure: unknown) {
    const status = (failure as { response?: { status?: number } })?.response
      ?.status;
    throw Object.assign(
      new Error('Replacement request could not be completed.'),
      { status }
    );
  }
}
export const invoiceReplacementAPI = {
  list: () => safe(apiClient.get<{ batches: ReplacementBatchView[] }>(base)),
  get: (id: string) => safe(apiClient.get<ReplacementBatchView>(batchPath(id))),
  review: (invoiceIds: string[], reason: string) =>
    safe(
      apiClient.post<ReplacementReviewView>(
        `${base}/review`,
        {
          invoiceIds,
          reason,
        },
        billingTimeout
      )
    ),
  prepare: (reviewToken: string) =>
    safe(
      apiClient.post<ReplacementBatchView>(
        `${base}/prepare`,
        {
          reviewToken,
          confirmed: true,
        },
        billingTimeout
      )
    ),
  execute: (id: string, fingerprint: string) =>
    safe(
      apiClient.post<ReplacementBatchView>(
        `${batchPath(id)}/execute`,
        {
          fingerprint,
          confirmed: true,
        },
        billingTimeout
      )
    ),
  recover: (id: string, fingerprint: string) =>
    safe(
      apiClient.post<ReplacementBatchView>(
        `${batchPath(id)}/recover`,
        {
          fingerprint,
          confirmed: true,
        },
        billingTimeout
      )
    ),
  cancel: (id: string, fingerprint: string) =>
    safe(
      apiClient.post<ReplacementBatchView>(
        `${batchPath(id)}/cancel`,
        {
          fingerprint,
          confirmed: true,
        },
        billingTimeout
      )
    ),
  collectionLink: (id: string, itemId: string) =>
    safe(
      apiClient.post<{ url: string; expiresAt: string }>(
        `${batchPath(id)}/items/${part(itemId)}/collection-link`,
        undefined,
        billingTimeout
      )
    ),
  historyPdf: (id: string, itemId: string) =>
    safe(
      apiClient.get<Blob>(
        `${batchPath(id)}/items/${part(itemId)}/history/pdf`,
        { ...billingTimeout, responseType: 'blob' }
      )
    ),
  publicGet: (token: string, portalToken: string) =>
    safe(
      publicApiClient.get<PublicReplacementView>(
        publicPath(token),
        portalAuth(portalToken)
      )
    ),
  confirm: (
    token: string,
    portalToken: string,
    consent: { amount: number; currency: string; consentVersion: string }
  ) =>
    safe(
      publicApiClient.post<ReplacementPaymentReceipt>(
        `${publicPath(token)}/confirm`,
        { ...consent, confirmed: true },
        portalAuth(portalToken)
      )
    ),
  status: (token: string, portalToken: string) =>
    safe(
      publicApiClient.get<ReplacementPaymentReceipt>(
        `${publicPath(token)}/status`,
        portalAuth(portalToken)
      )
    ),
};

export function replacementMoney(amount: number, currency: string) {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  });
  // Provider amounts use the currency's minor unit (including zero-decimal currencies).
  return `${formatter.format(
    amount / 10 ** (formatter.resolvedOptions().maximumFractionDigits ?? 2)
  )} ${currency.toUpperCase()}`;
}
